import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  // state: null = checking, false = anonymous, {user, company} = authenticated
  const [state, setState] = useState(getToken() ? null : false);

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setState(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setState({ user: data.user, company: data.company });
      return data;
    } catch {
      setToken(null);
      setState(false);
      return null;
    }
  }, []);

  useEffect(() => {
    if (getToken()) refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setState({ user: data.user, company: data.company });
    return data;
  }, []);

  const signup = useCallback(async (payload) => {
    const { data } = await api.post("/auth/signup", payload);
    setToken(data.access_token);
    setState({ user: data.user, company: data.company });
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    setToken(null);
    setState(false);
  }, []);

  return (
    <AuthCtx.Provider value={{ state, login, signup, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth outside AuthProvider");
  return ctx;
}
